import { Platform } from "react-native";
import { getMsalInstance } from "./msalInstance";
import { clearCachedToken, setCachedToken } from "./acquireTokenSilent";

// const MOBILE_SCOPE = "e3a34560-5d71-4055-b2d7-6ab366704411/.default";
const MOBILE_SCOPE = "2db662d1-27f1-432f-89b3-3efa2309d2dc/.default";
const MOBILE_PROMPT_SELECT_ACCOUNT = 0;
// Use WHEN_REQUIRED (3) for automatic/fallback token acquisition so the
// Authenticator account picker is NOT forced when a valid broker session
// already exists.  SELECT_ACCOUNT (0) must only be used for explicit
// user-initiated sign-in on shared devices.
const MOBILE_PROMPT_WHEN_REQUIRED = 3;

const getMobileWebviewParameters = () =>
  Platform.OS === "ios"
    ? {
        ios_prefersEphemeralWebBrowserSession: true,
      }
    : undefined;

/**
 * Helper function to extract expiry time from token response
 */
const extractExpiryTime = (response: any): number => {
  let expiryTime: number;
  if (
    response.expiresOn &&
    typeof response.expiresOn === "object" &&
    "getTime" in response.expiresOn
  ) {
    expiryTime = (response.expiresOn as Date).getTime();
  } else if (typeof response.expiresOn === "number") {
    expiryTime = response.expiresOn;
  } else if (typeof response.expiresOn === "string") {
    expiryTime = new Date(response.expiresOn).getTime();
  } else {
    expiryTime = Date.now() + 60 * 60 * 1000; // Default to 1 hour
  }
  return expiryTime;
};

/**
 * Acquire token using interactive authentication flow
 * This function should be used when silent token acquisition fails
 */
const acquireTokenInteractive = async () => {
  try {
    console.log("🔄 Starting interactive token acquisition");
    
    // Use the singleton MSAL instance
    const msalInstance = await getMsalInstance();

    if (Platform.OS === "web") {
      // For web, check if we're already in a redirect flow to avoid loops
      const urlParams = new URLSearchParams(window.location.search);
      const isRedirectCallback = urlParams.has('code') || urlParams.has('error');
      
      if (isRedirectCallback) {
        console.log("⚠️ Already in redirect flow, cannot trigger interactive login");
        return null;
      }

      // Check for existing accounts first
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log("🔍 Found existing accounts, trying popup first");
        try {
          // Try popup first to avoid redirect loops
          const result = await msalInstance.acquireTokenPopup({
            // Use API-specific scope for API access
            scopes: [process.env.MSAL_CLIENT_ID + "/.default"],
            account: accounts[0],
            prompt: "select_account",
          });
          
          if (result && result.accessToken) {
            const expiryTime = extractExpiryTime(result);
            setCachedToken(result.accessToken, expiryTime);
            console.log("✅ Successfully acquired token via popup for web");
            return result.accessToken;
          }
        } catch (popupError) {
          console.warn("❌ Popup login failed, falling back to redirect:", popupError);
        }
      }

      // Fallback to redirect if popup fails or no accounts
      console.log("🔄 Triggering interactive login redirect for web");
      await msalInstance.loginRedirect({
        // Use API-specific scope for API access
        scopes: [process.env.MSAL_CLIENT_ID + "/.default"],
        prompt: "select_account",
      });
      // This will reload the page, so we won't reach here
      return null;
    } else {
      // For mobile platforms, use interactive token acquisition
      console.log("🔄 Starting interactive token acquisition for mobile");
      
      // Use a hardcoded default client ID
      // This is safe because for mobile builds, msalInstance itself is initialized with the correct client ID
      // And that client ID is used for token acquisition regardless of what scope we provide here
      const result = await msalInstance.acquireToken({
        scopes: [MOBILE_SCOPE],
        // Use WHEN_REQUIRED so the broker (Authenticator) only opens when
        // there is truly no active session.  Using SELECT_ACCOUNT here causes
        // a redirect loop on shared iPads because the account picker is shown
        // even immediately after a successful authentication.
        promptType: MOBILE_PROMPT_WHEN_REQUIRED,
        webviewParameters: getMobileWebviewParameters(),
      });

      if (result && result.accessToken) {
        const expiryTime = extractExpiryTime(result);
        setCachedToken(result.accessToken, expiryTime);
        console.log("✅ Successfully acquired token interactively for mobile");
        return result.accessToken;
      } else {
        console.warn("⚠️ Interactive token acquisition returned no access token");
        return null;
      }
    }
  } catch (error) {
    console.error("❌ Error during interactive token acquisition:", error);
    clearCachedToken();
    
    // Don't throw error for web redirect flows as they're expected
    if (Platform.OS === "web" && error instanceof Error && error.message?.includes("redirect")) {
      console.log("🔄 Web redirect initiated successfully");
      return null;
    }
    
    throw error;
  }
};

export default acquireTokenInteractive;
